import React, { useState, useEffect } from 'react';
import { ArrowRight, Sparkles, Code2, Layout, Globe, Activity, Clock, Cpu, Layers, FileCode2 } from 'lucide-react';
import { motion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';

const Hero: React.FC = () => {
  const [isColor, setIsColor] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isTypingDone, setIsTypingDone] = useState(false);
  const { t, isUrdu } = useLanguage();

  const fullParagraph = t.hero.fullParagraph;

  useEffect(() => {
    let index = 0;
    setTypedText('');
    setIsTypingDone(false);
    const interval = setInterval(() => {
      if (index <= fullParagraph.length) {
        setTypedText(fullParagraph.slice(0, index));
        index++;
      } else {
        setIsTypingDone(true);
        clearInterval(interval);
      }
    }, 20);

    return () => clearInterval(interval);
  }, [fullParagraph]);

  const techIcons = [
    { icon: Code2, color: 'text-indigo-600' },
    { icon: FileCode2, color: 'text-orange-500' },
    { icon: Layout, color: 'text-blue-500' },
    { icon: Cpu, color: 'text-yellow-500' },
    { icon: Layers, color: 'text-blue-600' },
    { icon: Globe, color: 'text-teal-500' },
  ];

  return (
    <section 
      id="home" 
      itemScope 
      itemType="https://schema.org/Person"
      aria-label="Hero Introduction - Taibe Javed"
      className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden pt-24 pb-16"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-indigo-50/50 blur-[120px] rounded-full"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-50/50 blur-[120px] rounded-full"></div>
        <div className="absolute inset-0 hero-pattern opacity-60"></div>
        
        {/* Rotating Tech Icons */}
        {techIcons.map((item, index) => {
          const Icon = item.icon;
          const angle = (index / techIcons.length) * 360;
          return (
            <motion.div
              key={index}
              className={`absolute top-1/2 left-1/2 ${item.color} opacity-40`}
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                originX: "0px",
                originY: "0px",
                x: 240,
                rotate: angle,
              }}
            >
              <Icon size={28} />
            </motion.div>
          );
        })}
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-12 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Content: Text & Actions */}
          <div className="text-left animate-fade-in lg:pr-8">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 border border-slate-200 rounded-full">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
                  {t.hero.badgeAvailable}
                </span>
              </div>
              
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-50/80 border border-indigo-100 rounded-full shadow-sm">
                <Clock size={12} className="text-indigo-600" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-900">
                  {t.hero.badgeReadTime}
                </span>
              </div>
            </div>

            <h1 itemProp="name" className="text-5xl sm:text-6xl lg:text-7xl font-display font-extrabold tracking-tight text-slate-900 mb-6 leading-[1.1]">
              {t.hero.name}
            </h1>

            <div className="mb-6">
              <div className="h-[2px] w-24 bg-indigo-600 mb-6"></div>
              <h2 itemProp="jobTitle" className="text-xl sm:text-2xl font-bold tracking-tight text-slate-800 flex items-center flex-wrap gap-x-3 gap-y-1">
                {t.hero.titleLine}
              </h2>
            </div>

            <p itemProp="description" className="text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed mb-10 font-normal min-h-[90px] sm:min-h-[80px]">
              {typedText}
              <span className={`inline-block w-0.5 h-5 ml-1 bg-indigo-600 align-middle ${isTypingDone ? 'animate-pulse' : 'animate-ping'}`} />
            </p>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                aria-label="View my featured projects"
                className="px-8 py-4 rounded-xl bg-slate-900 hover:bg-indigo-600 text-white font-bold text-xs uppercase tracking-[0.2em] transition-all shadow-md active:scale-95 cursor-pointer"
              >
                {t.hero.btnExplore}
              </button>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                aria-label="Contact me for a new project"
                className="px-8 py-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-xs uppercase tracking-[0.2em] transition-all border border-slate-200 active:scale-95 cursor-pointer"
              >
                {t.hero.btnContact}
              </button>
            </div>
          </div>

          {/* Right Content: Circular Portrait */}
          <div className="relative flex justify-center lg:justify-end animate-fade-in [animation-delay:0.3s]">
            <div className="relative w-72 h-72 sm:w-[440px] sm:h-[440px] lg:w-[500px] lg:h-[500px]">
              
              {/* Decorative Rings */}
              <div className="absolute inset-[-20px] rounded-full border border-indigo-100 animate-spin-slow"></div>
              <div className="absolute inset-[-40px] rounded-full border border-slate-200 animate-reverse-spin"></div>
              <div className="absolute inset-0 rounded-full border-2 border-indigo-600/20 shadow-2xl"></div>
              
              {/* Main Circular Image Container */}
              <div 
                className="absolute inset-3 rounded-full overflow-hidden border-2 border-slate-200 shadow-xl group cursor-pointer bg-slate-100"
                onClick={() => setIsColor(!isColor)}
                role="button"
                aria-label="Toggle image color effect"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsColor(!isColor); }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-indigo-950/10 z-10"></div>
                <img 
                  src="https://i.pinimg.com/736x/af/4d/a5/af4da5dd0b6e4f8b84928fa7d15b41ca.jpg" 
                  className={`w-full h-full object-cover transition-all duration-700 ${isColor ? 'grayscale-0' : 'grayscale'} group-hover:grayscale-0`}
                  alt="Muhammad Taibe Javed - Professional WordPress Web Developer and AI Video Editor"
                  itemProp="image"
                  fetchPriority="high"
                  width="500"
                  height="500"
                  decoding="async"
                />
              </div>

              {/* Floating Badge - Positioned at bottom center */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-md border border-slate-200/90 px-5 py-2.5 rounded-full text-[10px] sm:text-[11px] font-extrabold text-slate-900 uppercase tracking-widest z-30 shadow-xl flex items-center gap-2 whitespace-nowrap">
                <span className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse" />
                <span>{t.hero.portraitBadge}</span>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;